import { Component, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { map, Observable, Subscription, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

import { BreadcrumbService } from '../../../services/breadcrumb.service';
import { Breadcrumb, BreadcrumbAction, BreadcrumbButton } from '../../../interfaces/utils/breadcrumb';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ApplicationStore } from '../../../shared/state/store/index';
import { DocumentsStore } from '../../../shared/state/reducers/documents.reducer';
import { DocumentsService, DownloadDocumentsFilters } from '../../../modules/documentos/services/documents.service';
import { loadingAction } from '../../../shared/state/actions/loading.action';
import { showAlert, showConfirmAlert } from '../../../utils/alert';
import { AutoevaluacionService } from '../../../modules/ruta/components/autoevaluacion/services/autoevaluacion.service';
import { tryJsonParse } from '../../../utils/string';
import { AutoevaluationStatus } from '../../../interfaces/autoevaluation/autoevaluation.entity';

@Component({
	selector: 'app-breadcrumb',
	templateUrl: './breadcrumb.component.html',
	styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnDestroy {
	@ViewChild('inputLoadExcel') inputLoadExcel!: ElementRef<HTMLInputElement>;

	breadcrumbs$: Observable<Breadcrumb[]>;
	breadcrumbsActive$: Observable<Breadcrumb | null>;

	activeSubscription = new Subscription();
	storeSubscription = new Subscription();
	actions: BreadcrumbAction[] = [];
	buttons: BreadcrumbButton[] = [];
	pageId: string = '';
	idInstitucion: string = '';
	documentsStore: DocumentsStore | null = null;
	showBack: boolean = false;
	isAdmin: boolean = false;

	constructor(
		breadcrumbService: BreadcrumbService,
		private ls: LocalStorageService,
		private store: Store<ApplicationStore>,
		private documentsService: DocumentsService,
		private autoevaluacionService: AutoevaluacionService,
		private location: Location,
		private router: Router
	) {
		this.isAdmin = this.ls.isAdmin;
		this.breadcrumbs$ = breadcrumbService.breadcrumbs$
			.pipe(
				// Ignorar el ultimo item del breadcrumb
				map(value => value.slice(0, value.length - 1)),
			);

		this.breadcrumbsActive$ = breadcrumbService.breadcrumbsActive$;
		this.activeSubscription = this.breadcrumbsActive$.subscribe(e => {
			if (e?.actions) {
				this.actions = e.actions;
			}
			if (e?.buttons) {
				this.buttons = e.buttons.filter(b => {
					if (b.admin && this.ls.isAdmin) {
						return true;
					} else if (!b.admin) {
						return true;
					}
					return false;
				});
			}
			if (e?.params) {
				if (e.params['id']) {
					this.pageId = e.params['id'];
				}
				if (e.params['idInstitucion']) {
					this.idInstitucion = e.params['idInstitucion'];
				}
			}
			if (e?.isModule !== undefined) {
				this.showBack = !e?.isModule;
			}
		});

		this.storeSubscription = this.store.select('documents').subscribe(state => {
			this.documentsStore = state;
		});
	}

	ngOnDestroy(): void {
		this.activeSubscription.unsubscribe();
		this.storeSubscription.unsubscribe();
	}

	excelUpload(event: Event) {
		const inputFile = event.target as HTMLInputElement;
		if (inputFile) {
			const file: File | null | undefined = inputFile.files?.item(0);
			if (file) {
				this.store.dispatch(loadingAction({ loading: true }));

				// Se crea autoevaluacion
				if (this.pageId === 'new') {
					const institutionId: string = this.idInstitucion;
					if (institutionId) {

						this.autoevaluacionService.getAutoevaluationsByInstitution(institutionId).subscribe(response => {
							if (response.length > 0) {
								this.router.navigate(['/not-found'], { replaceUrl: true });
								return;
							}

							this.autoevaluacionService.getBaseEvaluation().pipe(
								switchMap((base) => this.autoevaluacionService.createAutoevaluation(
									institutionId,
									base!.id,
									[],
									[]
								))
							).subscribe(autoevaluation => {
								this.loadExcel(autoevaluation.id, file);
							});
						});
					}
				} else {
					this.autoevaluacionService.getById(this.pageId).subscribe(auto => {
						if (auto.status !== AutoevaluationStatus.open) {
							this.store.dispatch(loadingAction({ loading: false }));
							showAlert('La autoevaluación no se encuentra abierta', undefined, 'info');
						} else {
							this.loadExcel(this.pageId, file);
						}
					});
				}

			}
		}
	}

	loadFromExcel(){
		const sendMessage = `Por favor verifique que el archivo de excel a cargar, cumpla con la misma estructura del siguiente archivo:
		<br>
		<a 
			href='../../assets/documents/autoevaluacion/excel_para_la_autoevaluacion_2022.xlsx' target="_blank" rel="noopener noreferrer"
			style='font-size: inherit;font-style: inherit;'
			download="Excel para la Autoevaluación"
		>
			Excel para la Autoevaluación
		</a>
		<br><br>
		Recuerde que también puede descargar el formato de excel para autoevaluación desde la sección de ayudas.`;

		showConfirmAlert({
			type: 'info',
			message: sendMessage,
			msgConfirm: 'Continuar',
			callBack: () => {
				this.inputLoadExcel.nativeElement.click();
			}
		});
	}

	downloadDocuments() {
		if (this.documentsStore) {
			this.store.dispatch(loadingAction({ loading: true }));
			const { id_category, id_document, id_institution } = this.documentsStore.filters;
			const filtersToSend: DownloadDocumentsFilters = {};
			if (id_category) {
				filtersToSend.id_category = id_category;
			}
			if (id_document) {
				filtersToSend.id_document = id_document;
			}
			if (id_institution) {
				filtersToSend.id_institution = id_institution;
			}

			this.documentsService.downloadAll(filtersToSend).subscribe((downloaded) => {
				if (downloaded) {
					this.store.dispatch(loadingAction({ loading: false }));
				} else {
					showAlert('Error descargando documentos', undefined, 'error');
				}
			});
		}
	}

	validateActions() {
		let hasActions = this.actions.length > 0;
		const hasExcel = this.actions.some(a => a.action === 'import_excel');

		if (this.isAdmin) {
			if (hasExcel && this.actions.length === 1) {
				return false;
			}
		}

		return hasActions;
	}

	goBack() {
		this.location.back();
	}

	private loadExcel(autoevaluationId: string, file: File) {
		this.autoevaluacionService.loadExcel(autoevaluationId, file).subscribe({
			complete: () => {
				this.store.dispatch(loadingAction({ loading: false }));

				showAlert('Autoevaluación actualizada', () => {
					if (this.pageId === 'new') {
						const url = this.ls.isAdmin && this.idInstitucion
							? '/ruta/autoevaluacion/institucion/' + this.idInstitucion + '/evaluar'
							: '/ruta/autoevaluacion/evaluar';

						window.location.href = url + '/' + autoevaluationId;
					} else {
						window.location.reload();
					}
				});
			},
			error: (error) => {
				const errorContent = tryJsonParse<{ message?: string }>(error.error);
				this.store.dispatch(loadingAction({ loading: false }));

				let message = 'Error en la carga del documento';
				if (errorContent?.message) {
					message = errorContent.message;
				}
				showAlert(message, undefined, 'error');
			}
		});
	}
}
